import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Flame, Info, Leaf, Minus, Plus, Repeat, ShoppingBag,
  Star, Truck, X,
} from "lucide-react";
import { FadeUp, Eyebrow } from "../components/Reveal";
import { HalalBadge } from "../components/Navbar";
import { api, formatApiError, imgSrc } from "../lib/api";
import {
  MEALS, EXTRAS, MIN_MEALS, PROMO, DELIVERY_FEE, FREE_DELIVERY_OVER,
  SUBSCRIPTION_RATE, DELIVERY_NOTICE, fmt,
} from "../data/site";
import { useSEO } from "../lib/useSEO";
import { getSessionId, trackBasket } from "../lib/activity";
import { useCustomer } from "../lib/auth";
import { useLocation, useNavigate } from "react-router-dom";

const TAG_TO_CAT = {
  "Chef Favorite": "chef",
  "High Protein": "protein",
  "Comfort": "comfort",
  "Plant-Forward": "plant",
  "Low Carb": "light",
};
const withCats = (m) => ({ ...m, cats: (m.tags || []).map((t) => TAG_TO_CAT[t]).filter(Boolean) });

const NUM_WORDS = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen", "twenty"];

const TABS = [
  { id: "all", label: "All", testid: "filter-all" },
  { id: "chef", label: "Chef Favorites", testid: "filter-chef" },
  { id: "protein", label: "High Protein", testid: "filter-protein" },
  { id: "comfort", label: "Comfort Classics", testid: "filter-comfort" },
  { id: "plant", label: "Plant-Forward", testid: "filter-plant" },
];

const MealCard = ({ meal, qty, onAdd, onRemove, onView, index }) => (
  <FadeUp delay={(index % 4) * 0.06}>
    <div
      className={`group flex h-full flex-col overflow-hidden rounded-3xl border bg-parchment transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-ink/10 ${
        qty > 0 ? "border-spice/60 ring-2 ring-spice/30" : "border-inkline"
      }`}
      data-testid={`meal-card-${meal.id}`}
    >
      <button className="relative block overflow-hidden text-left" onClick={() => onView(meal)} data-testid={`meal-view-${meal.id}`}>
        <img src={imgSrc(meal.img)} alt={meal.name} className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {meal.tags.map((t) => (
            <span key={t} className="rounded-full bg-ink/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-parchment backdrop-blur-sm">
              {t}
            </span>
          ))}
        </div>
      </button>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-lg font-semibold leading-snug text-ink">{meal.name}</h3>
          <span className="shrink-0 font-mono text-sm font-bold text-spice">{fmt(meal.price)}</span>
        </div>
        <p className="mt-1.5 line-clamp-2 flex-1 text-[13px] leading-relaxed text-muted2">{meal.desc}</p>
        <div className="mt-3 flex items-center gap-3 font-mono text-[11px] text-muted2">
          <span className="inline-flex items-center gap-1"><Flame size={11} className="text-spice" /> {meal.cal} cal</span>
          <span className="inline-flex items-center gap-1"><Leaf size={11} className="text-halal" /> {meal.protein}g protein</span>
        </div>
        <div className="mt-4">
          {qty === 0 ? (
            <button
              data-testid={`meal-add-${meal.id}`}
              onClick={() => onAdd(meal.id)}
              className="w-full rounded-full border-2 border-ink py-2.5 text-sm font-semibold text-ink transition-all duration-300 hover:bg-ink hover:text-parchment"
            >
              Add to Bundle
            </button>
          ) : (
            <div className="flex items-center justify-between rounded-full bg-ink p-1.5" data-testid={`meal-qty-${meal.id}`}>
              <button data-testid={`meal-minus-${meal.id}`} onClick={() => onRemove(meal.id)} className="flex h-9 w-9 items-center justify-center rounded-full bg-parchment/10 text-parchment transition-colors hover:bg-spice" aria-label={`Remove one ${meal.name}`}>
                <Minus size={15} />
              </button>
              <span className="font-mono text-sm font-bold text-parchment">{qty} in bundle</span>
              <button data-testid={`meal-plus-${meal.id}`} onClick={() => onAdd(meal.id)} className="flex h-9 w-9 items-center justify-center rounded-full bg-parchment/10 text-parchment transition-colors hover:bg-spice" aria-label={`Add one ${meal.name}`}>
                <Plus size={15} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  </FadeUp>
);

const QuickView = ({ meal, onClose, onAdd }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
    onClick={onClose}
    data-testid="quickview-modal"
  >
    <motion.div
      initial={{ y: 60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 60, opacity: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      onClick={(e) => e.stopPropagation()}
      className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-parchment sm:rounded-3xl"
    >
      <div className="relative">
        <img src={imgSrc(meal.img)} alt={meal.name} className="h-64 w-full object-cover sm:h-72" />
        <button data-testid="quickview-close" onClick={onClose} className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-parchment/90 text-ink backdrop-blur-sm transition-colors hover:bg-spice hover:text-parchment" aria-label="Close details">
          <X size={17} />
        </button>
      </div>
      <div className="p-7 sm:p-9">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h3 className="font-display text-2xl font-semibold text-ink sm:text-3xl">{meal.name}</h3>
          <span className="font-mono text-xl font-bold text-spice">{fmt(meal.price)}</span>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-muted2">{meal.desc}</p>
        <div className="mt-5 grid gap-4 rounded-2xl bg-cream p-5 sm:grid-cols-2">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-spice">Ingredients</div>
            <p className="mt-1.5 text-[13px] leading-relaxed text-ink/80">{meal.ingredients}</p>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-spice">Chef's note</div>
            <p className="mt-1.5 font-display text-[15px] italic leading-relaxed text-ink/85">“{meal.chef}”</p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 font-mono text-xs text-muted2">
          <span className="inline-flex items-center gap-1"><Flame size={12} className="text-spice" /> {meal.cal} calories</span>
          <span className="inline-flex items-center gap-1"><Leaf size={12} className="text-halal" /> {meal.protein}g protein</span>
          <HalalBadge />
        </div>
        <button
          data-testid="quickview-add-btn"
          onClick={() => { onAdd(meal.id); onClose(); }}
          className="mt-7 w-full rounded-full bg-spice py-3.5 text-sm font-bold text-parchment transition-colors duration-300 hover:bg-ink"
        >
          Add to Bundle — {fmt(meal.price)}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

const CheckoutModal = ({ cart, subscription, setSubscription, totals, onClose }) => {
  const { user } = useCustomer();
  const [form, setForm] = useState({
    customer_name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
    address: user?.address || "", city: user?.city || "", zip_code: user?.zip_code || "", notes: "",
  });
  const [promo, setPromo] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [testMode, setTestMode] = useState(false);

  useEffect(() => {
    api.get("/payments/config")
      .then(({ data }) => setTestMode(data.mode === "test"))
      .catch(() => {});
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const promoDiscount = promoApplied ? (totals.food - totals.subDiscount) * PROMO.rate : 0;
  const grandTotal = totals.food - totals.subDiscount - promoDiscount + totals.deliveryFee;

  const applyPromo = () => {
    if (promo.trim().toUpperCase() === PROMO.code) {
      setPromoApplied(true);
      toast.success(`${PROMO.code} applied — ${PROMO.label}!`);
    } else {
      toast.error("That code isn't valid. Try HIPLATE15 for your first order.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const items = cart.map(({ item, qty }) => ({ id: item.id, qty }));
      sessionStorage.setItem(
        "hiplate-last-order",
        JSON.stringify({ meals: cart.filter((c) => c.item.type === "meal").reduce((s, c) => s + c.qty, 0), total: grandTotal })
      );
      const { data } = await api.post("/payments/checkout", {
        items,
        subscription,
        promo_code: promoApplied ? PROMO.code : null,
        origin_url: window.location.origin,
        session_id: getSessionId(),
        ...form,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPlacing(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-inkline bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-muted2/60 focus:border-spice";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/60 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      data-testid="checkout-modal"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl bg-parchment sm:rounded-3xl"
      >
        <div className="flex items-center justify-between border-b border-inkline p-6 sm:px-9">
          <h3 className="font-display text-2xl font-semibold text-ink">Your delivery</h3>
          <button data-testid="checkout-close" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full bg-cream text-ink transition-colors hover:bg-spice hover:text-parchment" aria-label="Close checkout">
            <X size={17} />
          </button>
        </div>
        <div className="grid gap-8 p-6 sm:p-9 lg:grid-cols-5">
          <form onSubmit={submit} className="space-y-4 lg:col-span-3" data-testid="checkout-form">
            <button
              type="button"
              data-testid="subscription-toggle"
              onClick={() => setSubscription(!subscription)}
              className={`flex w-full items-center justify-between rounded-2xl border-2 p-4 text-left transition-all duration-300 ${
                subscription ? "border-spice bg-spice/5" : "border-inkline bg-cream hover:border-spice/50"
              }`}
            >
              <span className="flex items-center gap-3">
                <Repeat size={20} className={subscription ? "text-spice" : "text-muted2"} />
                <span>
                  <span className="block text-sm font-bold text-ink">Weekly subscription</span>
                  <span className="block text-xs text-muted2">Save 10% every week + earn HiPlate Points</span>
                </span>
              </span>
              <span className={`flex h-6 w-11 items-center rounded-full p-1 transition-colors ${subscription ? "bg-spice" : "bg-ink/20"}`}>
                <span className={`h-4 w-4 rounded-full bg-parchment transition-transform ${subscription ? "translate-x-5" : ""}`} />
              </span>
            </button>
            <div className="grid gap-4 sm:grid-cols-2">
              <input required data-testid="checkout-name" placeholder="Full name" value={form.customer_name} onChange={set("customer_name")} className={inputCls} />
              <input required type="email" data-testid="checkout-email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
              <input required data-testid="checkout-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} />
              <input required data-testid="checkout-zip" placeholder="ZIP code" value={form.zip_code} onChange={set("zip_code")} className={inputCls} />
            </div>
            <input required data-testid="checkout-address" placeholder="Street address" value={form.address} onChange={set("address")} className={inputCls} />
            <input required data-testid="checkout-city" placeholder="City" value={form.city} onChange={set("city")} className={inputCls} />
            <textarea data-testid="checkout-notes" placeholder="Delivery notes (gate code, drop-off spot…)" rows={2} value={form.notes} onChange={set("notes")} className={inputCls} />
            <p className="flex items-start gap-2 rounded-xl bg-saffron/10 p-3.5 text-xs leading-relaxed text-ink/80" data-testid="checkout-delivery-notice">
              <Truck size={15} className="mt-0.5 shrink-0 text-spice" /> {DELIVERY_NOTICE}
            </p>
            <button
              type="submit"
              data-testid="place-order-btn"
              disabled={placing}
              className="w-full rounded-full bg-spice py-4 text-sm font-bold text-parchment transition-all duration-300 hover:bg-ink disabled:opacity-60"
            >
              {placing ? "Redirecting to secure payment…" : `Pay ${fmt(grandTotal)}${testMode ? " — Test Mode" : ""}`}
            </button>
          </form>
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-inkline bg-cream p-5" data-testid="order-summary">
              <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-muted2">Bundle summary</h4>
              <div className="mt-4 max-h-52 space-y-2.5 overflow-y-auto pr-1">
                {cart.map(({ item, qty }) => (
                  <div key={item.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-ink/85">{qty} × {item.name}</span>
                    <span className="shrink-0 font-mono text-xs font-semibold text-ink">{fmt(item.price * qty)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-2 border-t border-inkline pt-4 text-sm">
                <div className="flex justify-between text-muted2"><span>Food subtotal</span><span className="font-mono">{fmt(totals.food)}</span></div>
                {totals.subDiscount > 0 && (
                  <div className="flex justify-between text-halal"><span>Subscription −10%</span><span className="font-mono">−{fmt(totals.subDiscount)}</span></div>
                )}
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-halal"><span>{PROMO.code} −15%</span><span className="font-mono">−{fmt(promoDiscount)}</span></div>
                )}
                <div className="flex justify-between text-muted2">
                  <span>DMV delivery</span>
                  <span className="font-mono">{totals.deliveryFee === 0 ? "FREE" : fmt(totals.deliveryFee)}</span>
                </div>
                <div className="flex justify-between border-t border-inkline pt-3 font-display text-lg font-bold text-ink">
                  <span>Total</span><span data-testid="order-total">{fmt(grandTotal)}</span>
                </div>
              </div>
              {!promoApplied && (
                <div className="mt-4 flex gap-2">
                  <input
                    data-testid="promo-input"
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    placeholder="Promo code"
                    className="w-full rounded-xl border border-inkline bg-parchment px-3.5 py-2.5 font-mono text-xs uppercase tracking-wider outline-none focus:border-spice"
                  />
                  <button type="button" data-testid="promo-apply-btn" onClick={applyPromo} className="shrink-0 rounded-xl bg-ink px-4 py-2.5 text-xs font-bold text-parchment transition-colors hover:bg-spice">
                    Apply
                  </button>
                </div>
              )}
            </div>
            <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted2" data-testid="checkout-payment-note">
              <Info size={14} className="mt-0.5 shrink-0" />
              {testMode
                ? "Secure card payment via Stripe — test mode: use card 4242 4242 4242 4242, any future date, any CVC."
                : "Secure card payment via Stripe. Your card is charged once; you'll get an email confirmation right away."}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function MealPrep() {
  useSEO({ title: "Halal Meal Prep Delivery — Weekly Menu | HiPlate", description: "Build your weekly bundle of chef-made 100% halal meals (5-meal minimum). Fresh, high-protein, delivered to your door across the DMV.", path: "/meal-prep" });
  const [tab, setTab] = useState("all");
  const [cartMap, setCartMap] = useState({});
  const [viewing, setViewing] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [subscription, setSubscription] = useState(false);

  const [meals, setMeals] = useState(() => MEALS.map(withCats));
  const [extras, setExtras] = useState(EXTRAS);
  const [menuReady, setMenuReady] = useState(false);
  useEffect(() => {
    Promise.allSettled([
      api.get("/menu").then(({ data }) => { if (Array.isArray(data) && data.length) setMeals(data.map(withCats)); }),
      api.get("/extras").then(({ data }) => { if (Array.isArray(data)) setExtras(data); }),
    ]).then(() => setMenuReady(true));
  }, []);

  const all = useMemo(() => [...meals.map((m) => ({ ...m, type: "meal" })), ...extras.map((e) => ({ ...e, type: "extra" }))], [meals, extras]);
  const byId = useMemo(() => Object.fromEntries(all.map((i) => [i.id, i])), [all]);

  // "Reorder this bundle" from the account page: rebuild the cart from a past order once the live menu is in.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const past = location.state?.reorder;
    if (!past || !menuReady) return;
    const byName = Object.fromEntries(all.map((i) => [i.name.toLowerCase(), i]));
    const next = {};
    const missing = [];
    for (const it of past) {
      const match = byId[it.id] || byName[it.name.toLowerCase()];
      if (match) next[match.id] = (next[match.id] || 0) + it.qty;
      else missing.push(it.name);
    }
    setCartMap(next);
    navigate(location.pathname, { replace: true, state: null });
    if (Object.keys(next).length) toast.success(missing.length ? `Bundle rebuilt — ${missing.join(", ")} ${missing.length > 1 ? "are" : "is"} no longer on the menu.` : "Your bundle is rebuilt — review and check out.");
    else toast.error("None of those dishes are on this week's menu — pick some new favorites.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuReady, location.state]);

  const add = (id) => setCartMap((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const remove = (id) =>
    setCartMap((c) => {
      const next = { ...c };
      if (next[id] > 1) next[id] -= 1;
      else delete next[id];
      return next;
    });

  const cart = Object.entries(cartMap).map(([id, qty]) => ({ item: byId[id], qty }));
  const mealCount = cart.filter((c) => c.item.type === "meal").reduce((s, c) => s + c.qty, 0);
  const food = cart.reduce((s, c) => s + c.item.price * c.qty, 0);
  const subDiscount = subscription ? food * SUBSCRIPTION_RATE : 0;
  const deliveryFee = food === 0 || food - subDiscount >= FREE_DELIVERY_OVER ? 0 : DELIVERY_FEE;
  const totals = { food, subDiscount, deliveryFee };
  const remaining = Math.max(0, MIN_MEALS - mealCount);
  const canCheckout = mealCount >= MIN_MEALS;

  useEffect(() => {
    if (cart.length) trackBasket(cart, mealCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartMap]);

  const filtered = tab === "all" ? meals : meals.filter((m) => m.cats.includes(tab));

  return (
    <main className="bg-parchment">
      <section className="grain relative overflow-hidden bg-ink pb-16 pt-40 text-parchment lg:pt-48" data-testid="mealprep-hero">
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-8">
          <HalalBadge />
          <h1 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            This week's <span className="italic text-saffron">{NUM_WORDS[meals.length] || meals.length}.</span>
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-parchment/70">
            A fresh rotation every week. Build a bundle of at least {MIN_MEALS} meals, add sides and
            desserts if you like, and we deliver it chilled to your door — anywhere in the DMV.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-saffron/40 bg-saffron/10 px-4 py-2 font-mono text-xs text-saffron">
              <ShoppingBag size={13} /> Min. {MIN_MEALS} meals per delivery
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-parchment/20 px-4 py-2 font-mono text-xs text-parchment/80">
              <Star size={13} className="text-saffron" /> First order: {PROMO.code} = 15% off
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-parchment/20 px-4 py-2 font-mono text-xs text-parchment/80">
              <Truck size={13} className="text-saffron" /> Delivery only · No pickup
            </span>
          </div>
        </div>
      </section>

      <div className="sticky top-[6.4rem] z-40 border-b border-inkline bg-parchment/90 backdrop-blur-xl sm:top-[6.9rem]" data-testid="filter-bar">
        <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 py-3.5 sm:px-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`shrink-0 rounded-full px-4.5 px-5 py-2 text-[13px] font-semibold transition-all duration-300 ${
                tab === t.id ? "bg-ink text-parchment" : "bg-cream text-ink/70 hover:bg-ink/10"
              }`}
            >
              {t.id === "all" ? `All ${meals.length}` : t.label}
            </button>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-7xl px-4 py-14 pb-32 sm:px-8" data-testid="meals-grid">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((meal, i) => (
            <MealCard key={meal.id} meal={meal} qty={cartMap[meal.id] || 0} onAdd={add} onRemove={remove} onView={setViewing} index={i} />
          ))}
        </div>

        <FadeUp className="mt-20">
          <div className="rounded-3xl border border-inkline bg-cream p-7 sm:p-10">
            <Eyebrow>Round out the table</Eyebrow>
            <h2 className="mt-2 font-display text-2xl font-semibold text-ink sm:text-3xl">Sides & desserts</h2>
            <p className="mt-2 max-w-lg text-sm text-muted2">
              Add-ons for your bundle — they don't count toward the {MIN_MEALS}-meal minimum, but they absolutely count at the table.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              {extras.map((extra) => {
                const qty = cartMap[extra.id] || 0;
                return (
                  <div key={extra.id} className="group rounded-2xl border border-inkline bg-parchment p-4 transition-all duration-300 hover:border-spice/50" data-testid={`extra-card-${extra.id}`}>
                    <img src={imgSrc(extra.img)} alt={extra.name} className="h-28 w-full rounded-xl object-cover" />
                    <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-spice">{extra.kind}</div>
                    <h3 className="mt-1 text-sm font-bold leading-snug text-ink">{extra.name}</h3>
                    {(extra.cal || extra.portion) && (
                      <p className="mt-1 text-[11px] text-muted2" data-testid={`extra-meta-${extra.id}`}>
                        {extra.portion}{extra.portion && extra.cal ? " · " : ""}{extra.cal ? `~${extra.cal} cal (est.)` : ""}
                      </p>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <span className="font-mono text-xs font-semibold text-muted2">{fmt(extra.price)}</span>
                      {qty === 0 ? (
                        <button data-testid={`extra-add-${extra.id}`} onClick={() => add(extra.id)} className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-parchment transition-colors hover:bg-spice" aria-label={`Add ${extra.name}`}>
                          <Plus size={14} />
                        </button>
                      ) : (
                        <div className="flex items-center gap-2" data-testid={`extra-qty-${extra.id}`}>
                          <button data-testid={`extra-minus-${extra.id}`} onClick={() => remove(extra.id)} className="flex h-7 w-7 items-center justify-center rounded-full bg-cream text-ink" aria-label={`Remove one ${extra.name}`}><Minus size={13} /></button>
                          <span className="font-mono text-xs font-bold">{qty}</span>
                          <button data-testid={`extra-plus-${extra.id}`} onClick={() => add(extra.id)} className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-parchment" aria-label={`Add one ${extra.name}`}><Plus size={13} /></button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </FadeUp>
      </section>

      <AnimatePresence>
        {mealCount > 0 && !checkoutOpen && (
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:pb-6"
            data-testid="bundle-bar"
          >
            <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink/10 bg-ink/95 px-5 py-4 text-parchment shadow-2xl shadow-ink/40 backdrop-blur-xl sm:rounded-full sm:px-7">
              <div className="flex items-center gap-3">
                <span className="relative flex h-10 w-10 items-center justify-center rounded-full bg-saffron font-mono text-sm font-bold text-ink">
                  {mealCount}
                </span>
                <div>
                  <div className="text-sm font-bold" data-testid="bundle-progress">
                    {canCheckout ? "Bundle ready" : `${mealCount}/${MIN_MEALS} meals — add ${remaining} more`}
                  </div>
                  <div className="h-1.5 w-36 overflow-hidden rounded-full bg-parchment/20">
                    <motion.div
                      className={`h-full rounded-full ${canCheckout ? "bg-halal" : "bg-saffron"}`}
                      animate={{ width: `${Math.min(100, (mealCount / MIN_MEALS) * 100)}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-mono text-sm font-bold text-saffron" data-testid="bundle-subtotal">{fmt(food)}</span>
                <button
                  data-testid="checkout-open-btn"
                  disabled={!canCheckout}
                  onClick={() => setCheckoutOpen(true)}
                  className="group inline-flex items-center gap-2 rounded-full bg-spice px-6 py-3 text-sm font-bold text-parchment transition-all duration-300 enabled:hover:bg-saffron enabled:hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Checkout <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {viewing && <QuickView meal={viewing} onClose={() => setViewing(null)} onAdd={add} />}
        {checkoutOpen && (
          <CheckoutModal
            cart={cart}
            subscription={subscription}
            setSubscription={setSubscription}
            totals={totals}
            onClose={() => setCheckoutOpen(false)}
          />
        )}
      </AnimatePresence>

      <div className="h-28" />
    </main>
  );
}
